import React, {useEffect, useState} from 'react'
import {
  useDeletePaymentAccountMutation,
  useUpdatePaymentAccountAutoPayMutation,
  useGetPaymentAccountsQuery,
  useGetPropertyQuery
} from '../../services/residentPortalApi'
import Loader from '../common/Loader'
import PaymentAccountList from './PaymentAccountList'
import {useAccountContext} from '../layout/AccountLayout'
import {useNavigate, useSearchParams} from 'react-router-dom'
import {findAutoPayAccount, findAutoPaySettings} from '../../services/utils'
import AutoPaySettingsModal from './AutoPaySettingsModal'
import ConfirmModal from '../common/ConfirmModal'
import {getNavigationPaths} from '../../services/navigation'
import Breadcrumbs from '../navigation/Breadcrumbs'
import PaymentTabs from '../payment/PaymentTabs'
import toast from 'react-hot-toast'
import {getLogger} from '../../services/logging'
import {PaymentAccount} from '../../types/app-types'
import Alert from '../common/Alert'

const logger = getLogger('PaymentAccountsPage')

const PaymentAccountsPage = () => {
  const {accountId, reservation} = useAccountContext()
  const propertyId = reservation.property.id
  const reservationId = reservation.id
  const navigate = useNavigate()
  const paths = getNavigationPaths(accountId)
  const [searchParams] = useSearchParams()
  const refresh = searchParams.get('refresh') === 'true'
  const [pollCount, updatePollCount] = useState(10)
  const paymentAccountsQuery = useGetPaymentAccountsQuery(propertyId)
  const paymentAccounts = paymentAccountsQuery.data
  const propertyQuery = useGetPropertyQuery(reservation.property.id)
  const property = propertyQuery.data

  // poll for payment accounts w/ max number of attempts
  useEffect(() => {
    if (refresh && pollCount > 0) {
      const {refetch} = paymentAccountsQuery
      setTimeout(() => {
        logger.logInfo('Polling for payment accounts...', {pollCount})
        refetch()
        updatePollCount(pollCount - 1)
      }, 15000)
    }
  }, [refresh, pollCount])

  // modal states and account selection
  const [modalState, setModalState] = useState({
    editAutoPayModalOpen: false,
    disableAutoPayModalOpen: false,
    removeAccountModalOpen: false
  })
  const [selectedAccount, setSelectedAccount] = useState<PaymentAccount>(null)

  // payment account mutations
  const [
    updateAutoPay,
    updateAutoPayResult
  ] = useUpdatePaymentAccountAutoPayMutation()
  const [
    deletePaymentAccount,
    deletePaymentAccountResult
  ] = useDeletePaymentAccountMutation()

  let content = null
  if (
    paymentAccountsQuery.isLoading ||
    updateAutoPayResult.isLoading ||
    deletePaymentAccountResult.isLoading ||
    propertyQuery.isLoading
  ) {
    content = <Loader />
  } else if (paymentAccounts && paymentAccounts.length) {
    const autoPayAccount = findAutoPayAccount(paymentAccounts, accountId)
    const autoPaySettings = findAutoPaySettings(autoPayAccount, accountId)
    content = (
      <>
        <Alert type="info" className="mb-6 w-full">
          <p>
            New payment accounts may take a few minutes to appear. Payment
            accounts will refresh automatically.
            <br />
            Please note: an additional{' '}
            <span className="font-bold">
              {property.creditCardFee * 100}%
            </span>{' '}
            transaction fee will be charged for credit or debit card payments.
          </p>
        </Alert>
        <PaymentAccountList
          paymentAccounts={paymentAccounts}
          autoPayAccount={autoPayAccount}
          autoPaySettings={autoPaySettings}
          onAddPaymentAccount={() => navigate(paths.AddPaymentAccount)}
          onRemovePaymentAccount={paymentAccount => {
            setSelectedAccount(paymentAccount)
            setModalState({...modalState, removeAccountModalOpen: true})
          }}
          onEditAutoPay={paymentAccount => {
            setSelectedAccount(paymentAccount)
            setModalState({...modalState, editAutoPayModalOpen: true})
          }}
          onEnableAutoPay={paymentAccount => {
            setSelectedAccount(paymentAccount)
            setModalState({...modalState, editAutoPayModalOpen: true})
          }}
          onDisableAutoPay={paymentAccount => {
            setSelectedAccount(paymentAccount)
            setModalState({...modalState, disableAutoPayModalOpen: true})
          }}
        />
        <AutoPaySettingsModal
          paymentAccount={selectedAccount}
          property={property}
          accountId={accountId}
          isAutoPayChanging={
            autoPayAccount && autoPayAccount !== selectedAccount
          }
          isOpen={modalState.editAutoPayModalOpen}
          onSave={settings => {
            logger.logInfo('Updating autoPay settings for payment account', {
              propertyId,
              reservationId,
              accountReference: selectedAccount.id,
              settings
            })

            setModalState({...modalState, editAutoPayModalOpen: false})

            const mutationPromise = updateAutoPay({
              propertyId,
              reservationId,
              accountReference: selectedAccount.id,
              ...settings
            }).unwrap()

            toast.promise(mutationPromise, {
              loading: 'AutoPay settings updating...',
              success: 'AutoPay settings updated',
              error: 'AutoPay settings update failed'
            })
          }}
          onClose={() =>
            setModalState({...modalState, editAutoPayModalOpen: false})
          }
        />
        <ConfirmModal
          title="Disable Autopay for Payment Account?"
          message="Are you sure you want to enable automatic payments for this payment account?"
          isOpen={modalState.disableAutoPayModalOpen}
          onConfirm={() => {
            logger.logInfo('Disabling autoPay for payment account', {
              propertyId,
              reservationId,
              accountReference: selectedAccount.id
            })

            setModalState({...modalState, disableAutoPayModalOpen: false})
            setSelectedAccount(null)

            const mutationPromise = updateAutoPay({
              propertyId,
              reservationId,
              accountReference: selectedAccount.id,
              autoPayEnabled: false,
              autoPayDayOfMonth: null,
              autoPayAmount: null
            }).unwrap()

            toast.promise(mutationPromise, {
              loading: 'AutoPay settings updating...',
              success: 'AutoPay account updated',
              error: 'AutoPay settings update failed'
            })
          }}
          onCancel={() => {
            setModalState({...modalState, disableAutoPayModalOpen: false})
            setSelectedAccount(null)
          }}
        />
        <ConfirmModal
          title="Remove Payment Account?"
          message={`Are you sure you want to enable automatic payments for this payment account?
          This will disable automatic payments for this account if enabled.`}
          isOpen={modalState.removeAccountModalOpen}
          onConfirm={() => {
            logger.logInfo('Deleting payment account', {
              paymentAccountRef: selectedAccount.id
            })
            setModalState({...modalState, removeAccountModalOpen: false})
            setSelectedAccount(null)

            const mutationPromise = deletePaymentAccount({
              accountReference: selectedAccount.id,
              propertyId,
              reservationId
            }).unwrap()

            toast.promise(mutationPromise, {
              loading: 'Payment account deleting...',
              success: 'Payment account deleted',
              error: 'Payment account deletion failed'
            })
          }}
          onCancel={() => {
            setModalState({...modalState, removeAccountModalOpen: false})
            setSelectedAccount(null)
          }}
        />
      </>
    )
  } else {
    content = (
      <div className="flex flex-col items-center">
        {refresh ? (
          <Alert type="info" className="mb-6 w-full">
            <p>
              New payment accounts may take a few minutes to appear. Payment
              accounts will refresh automatically.
              <br />
              Please note: an additional 2.5% transaction fee will be charged
              for credit or debit card payments.
            </p>
          </Alert>
        ) : (
          <Alert type="info" className="mb-6 w-full">
            <p>
              There are no payment accounts for this account. You can add
              payment accounts to make paying bills easier or to enroll in
              automatic payments.
              <br />
              Please note: an additional 2.5% transaction fee will be charged
              for credit or debit card payments.
            </p>
          </Alert>
        )}
        <a
          className="btn btn-primary"
          onClick={() => navigate(paths.AddPaymentAccount)}
        >
          Add a Payment Account
        </a>
      </div>
    )
  }

  return (
    <section>
      <Breadcrumbs
        items={[
          {label: 'Account', path: paths.Account, active: false},
          {label: 'Payment Accounts', active: true}
        ]}
      />

      <PaymentTabs accountId={accountId} />

      <div className="card-default w-full mx-auto">
        <div className="card-body-default">
          <h1 className="card-header">Payment Accounts</h1>
          <div>{content}</div>
        </div>
      </div>
    </section>
  )
}

export default PaymentAccountsPage
